<template>
  <b-modal
    id="confirm-save-cancel-question-modal"
    class="custom-modal"
    centered
    hide-header
    body-class="text-center pt-5"
    @shown="setConfirmType"
  >
    <template v-if="type === 'save'">
      <p>Are you sure you want to save your progress?</p>
    </template>
    <template v-else>
      <p class="mb-2">Are you sure you want to cancel?</p>
      <small class="pb-3 d-inline-block">All of your progress will be deleted.</small>
    </template>
    <div slot="modal-footer">
      <b-btn
        variant="ghost"
        class="mr-1 btn-cancel"
        @click="$root.$emit('bv::hide::modal', 'confirm-save-cancel-question-modal')"
      >
        Cancel
      </b-btn>
      <b-btn
        variant="primary"
        class="ml-1"
        @click="type === 'save' ? onSaveQuestion() : onCancelQuestion()"
      >
        {{ isLoading ? "Saving..." : "Confirm" }}
      </b-btn>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";

export default {
    name: "ConfirmSaveQuestionModal",
    data() {
        return {
            type: null
        };
    },
    computed: {
        ...mapGetters("surveys", [
            "questionProperties",
            "errors",
            "isLoading",
            "survey"
        ])
    },
    methods: {
        ...mapActions("surveys", [
            "createQuestion",
            "updateQuestion",
            "getQuestions"
        ]),
        ...mapMutations("surveys", ["setErrors", "setStringifyContent"]),
        setConfirmType() {
            this.type = this.questionProperties.type;
        },
        async onSaveQuestion() {
            const survey = this.$parent.survey;
            const question = survey.pages.find(
                page => page.uuid === this.questionProperties.page_uuid
            ).questions[this.questionProperties.questionIndex];

            const payload = {
                page_uuid: this.questionProperties.page_uuid,
                question: question.question,
                sort: Number(this.questionProperties.questionIndex),
                is_enabled: question.is_enabled,
                question_type: this.questionProperties.question_type,
                gigya_question_id: question.gigya_question_id //!= null
                    ? Number(question.gigya_question_id)
                    : null,
                image: question.image.base64 ? question.image.base64 : question.image.image,
            };

            if (this.questionProperties.question_uuid) {
                payload[
                    "question_uuid"
                ] = this.questionProperties.question_uuid;
            }

            if (this.questionProperties.question_type === "contact_details") {
                payload["content"] = question.contents.elements
                    .map(element => {
                        const elementObj = {
                            visible: element.visible,
                            required: element.required,
                            gigya_answer_id:
                                element.gigya_answer_id != null
                                    ? Number(element.gigya_answer_id)
                                    : null,
                            attributes: {
                                label:
                                    element.label !== null
                                        ? element.label
                                        : element.type,
                                placeholder: element.placeholder
                                    ? element.placeholder
                                    : element.type === "Email Address"
                                        ? "Enter your email here"
                                        : element.type === "Contact No."
                                            ? "09xxxxxxxxx"
                                            : `Enter your ${element.type.toLowerCase()} here`,
                                type: element.type
                            }
                        };
                        if (element.type === "Address")
                            elementObj.attributes[
                                "options"
                            ] = element.options.map(option => {
                                if (option.placeholder === null)
                                    option.placeholder = `Enter ${
                                        option.label
                                    }`;
                                return option;
                            });
                        return elementObj;
                    })
                    .flat();
            } else if (
                this.questionProperties.question_type === "multiple_choice" ||
                this.questionProperties.question_type === "checkbox" ||
                this.questionProperties.question_type === "dropdown"
            ) {
                payload["content"] = [
                    {
                        visible: question.contents.visible,
                        required: question.contents.required,
                        attributes: {
                            width: question.contents.width,
                            options: question.contents.elements.map(answer => {
                                return {
                                    answer: answer
                                };
                            }),
                            images: question.contents.images.map((image, index) => ({
                                "key": index,
                                "image": image.base64 ? image.base64 : image.image
                            })),
                            gigya_answer_ids: question.contents.gigya_answer_ids.map(
                                gigya_answer_id => {
                                    return {
                                        gigya_answer_id: gigya_answer_id
                                    };
                                }
                            )
                        }
                    }
                ];
                if (this.questionProperties.question_type === "checkbox")
                    payload["content"][0].attributes["limit"] =
                        question.contents.limit;
            } else if (this.questionProperties.question_type === "open_ended") {
                payload["content"] = [
                    {
                        visible: question.contents.visible,
                        required: question.contents.required,
                        gigya_answer_id:
                            question.contents.gigya_answer_id != null
                                ? Number(question.contents.gigya_answer_id)
                                : null,
                        attributes: {
                            char_length: question.contents.char_length,
                            placeholder: question.contents.placeholder
                                ? question.contents.placeholder
                                : "Type here"
                        }
                    }
                ];
            } else if (
                this.questionProperties.question_type === "file_upload"
            ) {
                payload["content"] = [
                    {
                        visible: question.contents.visible,
                        required: question.contents.required,
                        attributes: {
                            size_limit: question.contents.size_limit,
                            allowed_file_types:
                                question.contents.allowed_file_types
                        },
                        gigya_answer_id:
                            question.contents.gigya_answer_id != null
                                ? Number(question.contents.gigya_answer_id)
                                : null
                    }
                ];
            } else if (this.questionProperties.question_type === "rating") {
                payload["content"] = [
                    {
                        visible: question.contents.visible,
                        required: question.contents.required,
                        attributes: {
                            icon: question.contents.icon,
                            options: question.contents.elements.map(
                                (rating, index) => {
                                    return {
                                        value: index + 1,
                                        label: rating.label
                                    };
                                }
                            ),
                            gigya_answer_ids: question.contents.gigya_answer_ids.map(
                                gigya_answer_id => {
                                    return {
                                        gigya_answer_id:
                                            gigya_answer_id.gigya_answer_id
                                    };
                                }
                            )
                        }
                    }
                ];
            }

            if (this.questionProperties.question_uuid) {
                await this.updateQuestion(payload).then(() => {
                    this.$root.$emit(
                        "bv::hide::modal",
                        "confirm-save-cancel-question-modal"
                    );
                    if (
                        this.errors === null ||
                        Object.keys(this.errors).length === 0
                    )
                        this.$emit("question-saved");
                });
            } else {
                await this.createQuestion(payload).then(() => {
                    this.$root.$emit(
                        "bv::hide::modal",
                        "confirm-save-cancel-question-modal"
                    );
                    if (
                        this.errors === null ||
                        Object.keys(this.errors).length === 0
                    )
                        this.$emit("question-saved");

                });
            }

            setTimeout(() => {
                location.reload();
            }, 1000);
        },
        onCancelQuestion() {
            if (this.questionProperties.question_uuid) {
                this.setStringifyContent({
                    page_index: this.questionProperties.pageIndex,
                    question_uuid: this.questionProperties.question_uuid,
                    question_type: this.questionProperties.question_type
                });
                this.$nextTick(() => {
                    this.getQuestions({
                        page_uuid: this.questionProperties.page_uuid,
                        question_uuid: this.questionProperties.question_uuid
                    });
                });
            } else {
                this.resetForm();
            }

            this.setErrors(null);

            this.$root.$emit(
                "bv::hide::modal",
                "confirm-save-cancel-question-modal"
            );
            setTimeout(() => {
                location.reload();
            }, 1000);
        },
        resetForm() {
            const page = this.survey.pages.find(
                page => page.uuid === this.questionProperties.page_uuid
            );
            const default_multiple_choice = {
                elements: ["", ""],
                width: "full",
                visible: true,
                required: true
            };
            const default_contact_details = {
                elements: [
                    {
                        type: "First Name",
                        label: "First Name",
                        placeholder: null,
                        visible: true,
                        required: true
                    },
                    {
                        type: "Last Name",
                        label: "Last Name",
                        placeholder: null,
                        visible: true,
                        required: true
                    }
                ]
            };

            page.questions[
                this.questionProperties.questionIndex
            ].question = null;
            page.questions[
                this.questionProperties.questionIndex
            ].is_enabled = true;

            if (
                this.questionProperties.question_type === "multiple_choice" ||
                this.questionProperties.question_type === "checkbox" ||
                this.questionProperties.question_type === "dropdown"
            ) {
                // if (this.questionProperties.question_type === "checkbox") {
                //   default_multiple_choice["limit"] = null;
                // }
                if (
                    this.questionProperties.question_type === "checkbox" ||
                    this.questionProperties.question_type === "dropdown"
                ) {
                    default_multiple_choice.width = "half";
                }
                page.questions[
                    this.questionProperties.questionIndex
                ].contents = default_multiple_choice;
            } else if (
                this.questionProperties.question_type === "contact_details"
            ) {
                page.questions[
                    this.questionProperties.questionIndex
                ].contents = default_contact_details;
            } else if (this.questionProperties.question_type === "open_ended") {
                page.questions[
                    this.questionProperties.questionIndex
                ].contents = {
                    visible: true,
                    required: true,
                    char_length: null,
                    placeholder: null
                };
            } else if (
                this.questionProperties.question_type === "file_upload"
            ) {
                page.questions[
                    this.questionProperties.questionIndex
                ].contents = {
                    visible: true,
                    required: true,
                    size_limit: null,
                    allowed_file_types: []
                };
            } else if (this.questionProperties.question_type === "rating") {
                page.questions[
                    this.questionProperties.questionIndex
                ].contents = {
                    visible: true,
                    required: true,
                    icon: null,
                    elements: [
                        { label: "Poor" },
                        { label: "Bad" },
                        { label: "Good" },
                        { label: "Better" },
                        { label: "Best" }
                    ]
                };
            }
        }
    }
};
</script>
